import { FilterTag } from '@app/components/various/FilterTag';
import { Skeleton } from '@app/components/various/Skeleton';
import { BREAKPOINTS } from '@app/constants/breakpoints';
import { getSelectedValues, updateCheckboxTree } from '@app/logic/checkboxTree';
import { mapFilterOptionsToCheckboxOptions } from '@app/logic/filters';
import { useIsScreenSize } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Checkboxes, Filter, ProductFilters } from '@typings';
import cx from 'classnames';
import { without } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHover } from 'react-laag';

import { CollapsibleItemsList } from '../CollapsibleItemsList';

interface Props {
  filters: ProductFilters;
  attributes: Filter.Field<string>[];
  isCollapsible?: boolean;
  isLoading: boolean;
  onFilterChange: (newFilters: ProductFilters) => void;
}

const OVERFLOW_OFFSET = 10;
const VERTICAL_OFFSET = -32;
const HORIZONTAL_OFFSET = -6;

export const AppliedFilters = ({ filters, attributes, onFilterChange, isCollapsible = false, isLoading }: Props) => {
  const { t } = useTranslation(['common']);
  const isTablet = useIsScreenSize(BREAKPOINTS.LG);
  const selectedAttributeGroups = React.useMemo(() => {
    const filterKeys = Object.keys(filters);

    return attributes.filter(({ field, isRadio }) => filterKeys.includes(field) && !isRadio);
  }, [filters, attributes]);

  const getActiveFiltersInGroup = React.useCallback(
    (group: Filter.Field<string>) => {
      return group.values.filter(value => filters[group.field]?.includes(value.filterValue));
    },
    [filters],
  );

  const handleFilterChange = React.useCallback(
    (fieldName: keyof ProductFilters, selectedValues: Filter.Value | Filter.Value[]) => {
      window.scrollTo({ top: 0 });
      onFilterChange({ [fieldName]: selectedValues });
    },
    [onFilterChange],
  );

  const attributesFilters = React.useMemo(
    () =>
      selectedAttributeGroups.reduce<{ [attribute: string]: Filter.Option<string>[] }>((acc, group) => {
        const activeFilters = getActiveFiltersInGroup(group);
        if (activeFilters.length === 0) {
          return acc;
        }

        return { ...acc, [group.field]: activeFilters };
      }, {}),
    [getActiveFiltersInGroup, selectedAttributeGroups],
  );

  const selectedAvailabilityFilter = React.useMemo(() => {
    const onlyAvailableData = attributes.find(({ field }) => field === 'onlyAvailable');

    return onlyAvailableData?.values.find(({ filterValue }) => filterValue === filters.onlyAvailable);
  }, [attributes, filters.onlyAvailable]);

  const clearIsStockFilter = React.useCallback(() => {
    handleFilterChange('onlyAvailable', 'no');
  }, [handleFilterChange]);

  const getAllOptionsForAttribute = React.useCallback(
    (attribute: string) => attributes.find(attrib => attrib.field === attribute)?.values ?? [],
    [attributes],
  );

  const clearAttributeFilter = (filterValue: string, attribute: string) => () => {
    const allOptions = mapFilterOptionsToCheckboxOptions(getAllOptionsForAttribute(attribute));
    const hasNested = allOptions.some(option => isDefined(option.parentValue));

    if (hasNested) {
      const baseState = attributesFilters[attribute]?.reduce<Checkboxes.OptionsState>(
        (acc, option) => ({
          ...acc,
          [option.value]: 'checked',
        }),
        {},
      );

      const updatedNestedFilters = updateCheckboxTree(allOptions)(filterValue, 'unchecked', baseState);
      handleFilterChange(attribute, getSelectedValues(updatedNestedFilters));

      return;
    }

    handleFilterChange(attribute, without([filterValue], [...(filters[attribute] ?? [])]));
  };

  const clearSearchFilter = React.useCallback(() => {
    handleFilterChange('search', '');
    window.scrollTo({ top: 0 });
  }, [handleFilterChange]);

  const searchFilterName = (
    <>
      <span>{t('common:search')}:&nbsp;</span>
      <span data-sentry-mask="true">{filters.search}</span>
    </>
  );

  const hasAvailabilityFilter = isDefined(selectedAvailabilityFilter) && selectedAvailabilityFilter.filterValue !== 'no';
  const hasSearchFilter = isDefined(filters.search) && !isEmpty(filters.search);
  const hasAttributeFilters = !isEmpty(attributesFilters);

  const hasAnyFilters = hasAvailabilityFilter || hasSearchFilter || hasAttributeFilters;
  const attributesFiltersCount = Object.values(attributesFilters).reduce((acc, items) => acc + items.length, 0);
  const singularFiltersCount = [hasAvailabilityFilter, hasSearchFilter].filter(Boolean).length;
  const totalFiltersCount = attributesFiltersCount + singularFiltersCount;
  const message = hasAnyFilters ? t('common:applied_filters', { count: totalFiltersCount }) : t('common:no_filters_applied');

  const [isOver, hoverProps] = useHover({
    hideOnScroll: false,
  });

  const content = (
    <>
      {hasAvailabilityFilter && <FilterTag name={selectedAvailabilityFilter.data} onClick={clearIsStockFilter} />}
      {Object.entries(attributesFilters).map(([key, value]) =>
        value.map(element => (
          <FilterTag
            key={element.value}
            name={element.data}
            swatchData={element.swatch}
            onClick={clearAttributeFilter(element.filterValue, key)}
            className={cx({ invisible: isTablet && isCollapsible && !isOver })}
          />
        )),
      )}
      {hasSearchFilter && <FilterTag key="clearSearchFilter" name={searchFilterName} onClick={clearSearchFilter} />}
    </>
  );

  if (isLoading) {
    return (
      <div className="w-full max-w-28">
        <Skeleton type="text" />
      </div>
    );
  }

  if (isCollapsible) {
    return (
      <CollapsibleItemsList
        overflowOffset={OVERFLOW_OFFSET}
        popoverVerticalOffset={VERTICAL_OFFSET}
        popoverHorizontalOffset={HORIZONTAL_OFFSET}
        popoverClassName="bg-neutral-96 p-2 gap-2 items-center"
        triggerClassName="h-6 gap-2 items-center"
        externalHoverProps={hoverProps}
        showOverflowingCount={!isTablet}
      >
        <span className="h-6 py-1 text-neutral-60 heading-4">{message}</span>
        {content}
      </CollapsibleItemsList>
    );
  }

  return content;
};
